import type { Router } from 'vue-router'
import { usePermissionStore } from '@/stores/permission'
import type { PermissionedRouteCandidate } from '@/config/businessRoutePermissions'
import type { PermissionRequirement } from '@/types/permission'
import { clearAuthClientState, hasMustChangePasswordFlag } from '@/utils/authState'
import { getDefaultRoute, getFirstAccessibleRoute } from '@/utils/defaultRoute'

export function setupRouterGuards(router: Router) {
  router.beforeEach(async (to, _from, next) => {
    const publicPages = ['/login', '/register', '/forgot-password', '/sso/callback', '/403']
    const authRequired = !publicPages.includes(to.path)
    const user = localStorage.getItem('user')
    const token = localStorage.getItem('token')
    const isAuthenticated = Boolean(user && token)

    if (authRequired && !isAuthenticated) {
      clearAuthClientState()
      next({ path: '/login', query: { redirect: to.fullPath } })
      return
    }

    if (!authRequired) {
      next()
      return
    }

    if (isAuthenticated) {
      try {
        const parsedUser = JSON.parse(user || '{}')
        if (hasMustChangePasswordFlag(parsedUser) && to.path !== '/force-change-password') {
          next('/force-change-password')
          return
        }
      } catch {
        // ignore malformed local user state; normal auth checks handle it
      }

      const permStore = usePermissionStore()
      permStore.loadPermissions()
      const { ensureAuthSessionReady } = await import('@/services/authSession')
      const sessionResult = await ensureAuthSessionReady({ reason: 'router.beforeEach' })

      if (!sessionResult.ok) {
        clearAuthClientState()
        next({ path: '/login', query: { redirect: to.fullPath } })
        return
      }

      if (to.path === '/') {
        next(getDefaultRoute(permStore.permissions))
        return
      }

      const defaultRoute = getMatchedDefaultRoute(to, permStore.permissions)
      if (defaultRoute && defaultRoute !== to.fullPath) {
        next(defaultRoute)
        return
      }

      // 权限路由检查（如果路由配置了 meta.permission）
      const requiredPermission = to.meta?.permission as PermissionRequirement | undefined
      if (requiredPermission && !permStore.canAccess(requiredPermission)) {
        next('/403')
        return
      }
    }

    next()
  })
}

function getMatchedDefaultRoute(to: any, permissions: string[]): string | undefined {
  const matchedWithDefault = [...(to.matched || [])].reverse().find((record: any) => {
    return record.meta?.defaultRouteBasePath === to.path
      && Array.isArray(record.meta?.defaultRouteCandidates)
  })

  if (!matchedWithDefault) return undefined

  return getFirstAccessibleRoute(
    matchedWithDefault.meta.defaultRouteCandidates as PermissionedRouteCandidate[],
    permissions
  )
}
